import React from "react";
import { Team } from "../types/tournament";
import { Trophy, TrendingUp, TrendingDown, Minus } from "lucide-react";

interface StandingsBoardProps {
  teams: Team[];
}

const StandingsBoard: React.FC<StandingsBoardProps> = ({ teams }) => {
  // Sort teams by current court (1 is best), then by wins, then by win percentage
  const sortedTeams = [...teams].sort((a, b) => {
    // First sort by current court (lower number = better)
    if (a.currentCourt !== b.currentCourt) {
      return a.currentCourt - b.currentCourt;
    }

    // Then by wins
    if (a.wins !== b.wins) {
      return b.wins - a.wins;
    }

    // Then by win percentage
    const aWinPct = a.wins / (a.wins + a.losses);
    const bWinPct = b.wins / (b.wins + b.losses);
    return bWinPct - aWinPct;
  });

  const getMovementIcon = (movement: number) => {
    if (movement < 0) {
      return <TrendingUp className="w-4 h-4 text-success-600" />;
    } else if (movement > 0) {
      return <TrendingDown className="w-4 h-4 text-red-600" />;
    } else {
      return <Minus className="w-4 h-4 text-gray-400" />;
    }
  };

  const getMovementText = (movement: number) => {
    if (movement < 0) {
      return `+${Math.abs(movement)}`;
    } else if (movement > 0) {
      return `-${movement}`;
    } else {
      return "0";
    }
  };

  const getMovementColor = (movement: number) => {
    if (movement < 0) {
      return "text-success-600";
    } else if (movement > 0) {
      return "text-red-600";
    } else {
      return "text-gray-500";
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-4">
          Tournament Standings
        </h3>
        <p className="text-gray-600 mb-6">
          Teams ranked by current court position, wins, and win percentage
        </p>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm sm:text-base">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rank
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Team
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Court
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Record
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Win %
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Movement
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedTeams.map((team, index) => {
                const winPercentage =
                  team.wins + team.losses > 0
                    ? ((team.wins / (team.wins + team.losses)) * 100).toFixed(1)
                    : "0.0";

                const isChampion = team.currentCourt === 1;

                return (
                  <tr
                    key={team.id}
                    className={`hover:bg-gray-50 ${
                      isChampion
                        ? "bg-yellow-50 border-l-4 border-yellow-400"
                        : ""
                    }`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {index === 0 && isChampion && (
                          <Trophy className="w-5 h-5 text-yellow-500 mr-2" />
                        )}
                        <span
                          className={`text-sm font-medium ${
                            isChampion ? "text-yellow-800" : "text-gray-900"
                          }`}
                        >
                          {index + 1}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div
                          className={`text-sm font-medium ${
                            isChampion ? "text-yellow-800" : "text-gray-900"
                          }`}
                        >
                          {team.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {team.player1} & {team.player2}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          team.currentCourt === 1
                            ? "bg-yellow-100 text-yellow-800"
                            : team.currentCourt <= 3
                            ? "bg-green-100 text-green-800"
                            : team.currentCourt <= 6
                            ? "bg-blue-100 text-blue-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        Court {team.currentCourt}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {team.wins}W - {team.losses}L
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {winPercentage}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getMovementIcon(team.totalMovement)}
                        <span
                          className={`ml-1 text-sm font-medium ${getMovementColor(
                            team.totalMovement
                          )}`}
                        >
                          {getMovementText(team.totalMovement)}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Legend</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="flex items-center">
            <Trophy className="w-4 h-4 text-yellow-500 mr-2" />
            <span className="text-gray-700">Champion (Court 1)</span>
          </div>
          <div className="flex items-center">
            <TrendingUp className="w-4 h-4 text-success-600 mr-2" />
            <span className="text-gray-700">Moving Up</span>
          </div>
          <div className="flex items-center">
            <TrendingDown className="w-4 h-4 text-red-600 mr-2" />
            <span className="text-gray-700">Moving Down</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StandingsBoard;
